import { createClient } from "@supabase/supabase-js";

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  // eslint-disable-next-line no-console
  console.error(
    "[Supabase] REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY must be set at build time.",
  );
}

export const supabase = createClient(SUPABASE_URL ?? "", SUPABASE_ANON_KEY ?? "", {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
});

/**
 * Recovers the JSON body an Edge Function actually returned.
 *
 * supabase.functions.invoke() only surfaces the function's `{error}` body in
 * `data` when it answered 2xx. For any non-2xx (our functions use 400
 * no_api_key, 401 invalid_api_key, 403 forbidden, 500 insert failure, 502
 * upstream Anthropic failure) it sets `data` to null and `error` to a generic
 * FunctionsHttpError whose message is the useless "Edge Function returned a
 * non-2xx status code" — the real reason survives only on error.context, the
 * raw Response.
 *
 * Returns the parsed body ({} shape) or null when there was nothing readable.
 * .clone() so the caller can still read error.context itself if it wants.
 */
export async function resolveFunctionPayload(error, data) {
  if (data && typeof data === "object") return data;
  if (error?.context?.clone) {
    try {
      return await error.context.clone().json();
    } catch { /* body wasn't JSON — fall through */ }
  }
  return null;
}

/**
 * Digs a human-readable sentence out of whatever an Edge Function put in its
 * `error` field. Anthropic failures arrive nested as
 * { error: { type: "error", error: { type, message } } }, and JSON.stringify-ing
 * that whole blob into a toast is what previously hid the real cause.
 */
export function describeFunctionError(err) {
  let cur = err;
  for (let depth = 0; depth < 4 && cur && typeof cur === "object"; depth++) {
    if (typeof cur.message === "string" && cur.message) return cur.message;
    cur = cur.error ?? cur.detail ?? cur.msg;
  }
  if (typeof cur === "string" && cur) return cur;
  if (typeof err === "string" && err) return err;
  try { return JSON.stringify(err); } catch { return String(err); }
}

/** Call an Edge Function with automatic auth passthrough. */
export async function callFn(name, body) {
  const { data, error } = await supabase.functions.invoke(name, { body });
  if (error) throw error;
  return data;
}
